import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import * as forge from 'node-forge';

import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaCamera, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaArrowLeft,
  FaHardHat,
  FaRegHospital,
  FaDownload,
  FaStar,
  FaBriefcase,
  FaGraduationCap,
  FaTools,
  FaEye,
  FaIdCard,
  FaImage,
  FaQrcode,
  FaFileSignature,
  FaCheckCircle,
  FaUpload
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';
import ExportPDFButton from '../components/ExportPDFButton';


interface Skill {
  id: string;
  name: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  category: 'technical' | 'soft' | 'safety' | 'certification' | 'other';
  yearsOfExperience: number;
  isCertified: boolean;
  certificationDetails?: string;
  createdAt: string;
}

interface UserProfile {
  // Información básica
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  sector: 'health' | 'construction';
  role: 'worker' | 'premiumWorker' | 'specialWorker' | 'admin' | 'business' | 'employee';
  status: 'active' | 'pending';
  language: string;
  
  // Foto de perfil
  photoURL: string;
  
  // QR Code del perfil público
  qrCodeUrl?: string;
  
  // Dirección
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    apartment?: string;
    number?: string;
    verificationStatus?: 'pending' | 'approved' | 'rejected';
  };
  
  // Códigos de ubicación
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  
  // Información adicional
  additionalInfo?: string;
  personalDescription?: string;
  
  // Experiencia
  experience: {
    years: number;
    skills: Array<{
      id: string;
      categoryId: string;
      name_en: string;
      name_es: string;
    }>;
    description: string;
  };
  
  // Certificaciones
  certifications: Array<{
    id?: string;
    name: string;
    name_en?: string;
    name_es?: string;
    issuer: string;
    issuer_en?: string;
    issuer_es?: string;
    date: string;
    expiryDate?: string;
    status: 'approved' | 'rejected' | 'pending';
    fileUrl?: string;
    frontImageUrl?: string;
    backImageUrl?: string;
    description_en?: string;
    description_es?: string;
    duration_en?: string;
    duration_es?: string;
    certificationId?: string;
    uploadedAt?: string;
  }>;
  
  // Identificación
  identification?: {
    type: string;
    number: string;
    frontImageUrl: string;
    backImageUrl: string;
    verificationStatus: 'pending' | 'approved' | 'rejected';
    updatedAt: string;
  };
  
  // Certificado de Firma Electrónica
  digitalSignature?: {
    certificateUrl: string;
    certificateName: string;
    uploadedAt: string;
    expiryDate?: string;
    status: 'active' | 'expired' | 'revoked';
    issuerName?: string;
    subjectName?: string;
    organization?: string;
    country?: string;
    validFrom?: string;
    validTo?: string;
    serialNumber?: string;
  };
  
  // Ubicación
  lastKnownLocation?: {
    lat: number;
    lon: number;
    timestamp: string;
  };
  
  // Estados de verificación
  verificationRead?: boolean;
  verificationReadAt?: string;
  
  // Estados de rechazo
  addressRejected?: boolean;
  addressRejectedAt?: string;
  addressRejectedBy?: string;
  addressRejectionNotes?: string;
  
  identificationRejected?: boolean;
  identificationRejectedAt?: string;
  identificationRejectedBy?: string;
  identificationRejectionNotes?: string;
  
  // Estados de bloqueo
  blockedAt?: string;
  blockedBy?: string;
  blockReason?: string | null;
  blockReasonDetails?: string | null;
  unblockedAt?: string;
  unblockedBy?: string;
  
  // Onboarding
  onBoarding: boolean;
  completedSteps: string[];
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  
  // Campos legacy (mantener por compatibilidad)
  skills?: Skill[];
  completedJobs?: number;
  rating?: number;
  reviews?: number;
  lastActive?: string;
}

const WorkerProfile: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Función para obtener el nombre de la categoría
  const getCategoryName = (categoryId: string) => {
    const categories: Record<string, Record<string, { en: string; es: string }>> = {
      construction: {
        cat001: { en: "General Construction", es: "Construcción General" },
        cert001: { en: "General Safety Certifications", es: "Certificaciones Generales de Seguridad" },
        cert002: { en: "Environmental Hazard Certifications", es: "Certificaciones de Riesgos Ambientales" },
        cert003: { en: "Mold Certifications", es: "Certificaciones de Moho" },
        cert004: { en: "Worksite Safety Certifications", es: "Certificaciones de Seguridad en el Sitio" }
      },
      health: {
        // Aquí agregarías las categorías de salud cuando las tengas
        cat001: { en: "General Health", es: "Salud General" }
      }
    };

    const sectorCategories = categories[profile?.sector || 'construction'];
    const category = sectorCategories?.[categoryId];
    
    if (category) {
      return i18n.language === 'es' ? category.es : category.en;
    }
    
    return categoryId; // Fallback al ID si no se encuentra la categoría
  };
  const db = getFirestore();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingIdFront, setUploadingIdFront] = useState(false);
  const [uploadingIdBack, setUploadingIdBack] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [pendingCertificateFile, setPendingCertificateFile] = useState<File | null>(null);
  const [validatingCertificate, setValidatingCertificate] = useState(false);
  
  // Estados para edición
  const [editData, setEditData] = useState<Partial<UserProfile>>({});

  useEffect(() => {
    fetchProfile();
  }, [currentUser]);

  // Función para generar QR code
  const generateAndSaveQRCode = async () => {
    if (!currentUser) return;
    
    try {
      const publicProfileUrl = `https://brix-v2.web.app/worker/${currentUser.uid}`;
      console.log('Generating QR for URL:', publicProfileUrl);
      
      // Generar QR code como SVG
      const qrSvg = await QRCode.toString(publicProfileUrl, {
        type: 'svg',
        width: 200,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      // Convertir SVG a data URL
      const qrDataUrl = `data:image/svg+xml;base64,${btoa(qrSvg)}`;
      
      // Guardar en Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        qrCodeUrl: qrDataUrl,
        updatedAt: new Date().toISOString()
      });
      
      console.log('QR Code saved to Firestore');
      
      // Actualizar el estado local
      setProfile(prev => prev ? { ...prev, qrCodeUrl: qrDataUrl } : null);
      
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const fetchProfile = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfile(userData as UserProfile);
        setEditData(userData);
        
        // Generar QR code si no existe
        if (!userData.qrCodeUrl) {
          console.log('No QR code found, generating...');
          await generateAndSaveQRCode();
        }
      } else {
        // Si no existe el documento, crear un perfil inicial vacío
        console.log('No profile found, creating initial profile...');
        
        // Intentar obtener el rol desde el AuthContext o usar worker por defecto
        const userRole = (userData?.role as UserProfile['role']) || 'worker';
        
        const initialProfile: Partial<UserProfile> = {
          firstName: '',
          lastName: '',
          email: currentUser.email || '',
          phone: '',
          sector: 'construction',
          role: userRole,
          status: 'pending',
          language: 'es',
          photoURL: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: ''
          },
          experience: {
            years: 0,
            skills: [],
            description: ''
          },
          certifications: [],
          onBoarding: false,
          completedSteps: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        setProfile(initialProfile as UserProfile);
        setEditData(initialProfile);
        setIsEditing(true); // Activar modo edición automáticamente
      }
    } catch (err) {
      setError(t('profile.error.loading'));
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser || !editData) return;
    
    try {
      setSaving(true);
      setError('');
      
      // Función para limpiar valores undefined recursivamente
      const cleanData = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        
        if (Array.isArray(obj)) {
          return obj.map(item => cleanData(item));
        }
        
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const key in obj) {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = cleanData(value);
            }
          }
          return cleaned;
        }
        
        return obj;
      };
      
      // Verificar si el documento existe
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      const dataToSave = cleanData({
        ...editData,
        updatedAt: new Date().toISOString()
      });
      
      if (!userDoc.exists()) {
        // Si no existe, crear el documento con setDoc
        const { setDoc } = await import('firebase/firestore');
        await setDoc(userDocRef, {
          ...dataToSave,
          createdAt: new Date().toISOString()
        });
        console.log('Profile created successfully');
      } else {
        // Si existe, actualizar con updateDoc
        await updateDoc(userDocRef, dataToSave);
        console.log('Profile updated successfully');
      }
      
      setProfile(prev => prev ? { ...prev, ...editData } : editData as UserProfile);
      setIsEditing(false);
      setSuccess(t('profile.success.updated'));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('profile.error.updating'));
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData(profile || {});
    setIsEditing(false);
    setError('');
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      setUploadingPhoto(true);
      setError('');

      // Eliminar foto anterior si existe
      if (profile?.photoURL) {
        try {
          const oldPhotoRef = ref(storage, profile.photoURL);
          await deleteObject(oldPhotoRef);
        } catch (err) {
          console.log('No previous photo to delete');
        }
      }

      // Subir nueva foto
      const photoRef = ref(storage, `users/${currentUser.uid}/profile/${Date.now()}_${file.name}`);
      await uploadBytes(photoRef, file);
      const photoUrl = await getDownloadURL(photoRef);

      // Actualizar perfil
      await updateDoc(doc(db, 'users', currentUser.uid), {
        photoURL: photoUrl,
        updatedAt: new Date().toISOString()
      });

      setProfile(prev => prev ? { ...prev, photoURL: photoUrl } : null);
      setEditData(prev => ({ ...prev, photoURL: photoUrl }));
      setSuccess(t('profile.success.photoUpdated'));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('profile.error.photoUpload'));
      console.error('Error uploading photo:', err);
    } finally {
      setUploadingPhoto(false);
    }
  };



  // Funciones para manejar identificación
  const handleIdFrontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentUser || !e.target.files?.[0]) return;
    
    try {
      setUploadingIdFront(true);
      const file = e.target.files[0];
      const fileExtension = file.name.split('.').pop();
      const fileName = `front_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `users/${currentUser.uid}/id/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Eliminar imagen anterior si existe
      if (profile?.identification?.frontImageUrl) {
        try {
          const oldImageRef = ref(storage, profile.identification.frontImageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.log('No previous image to delete');
        }
      }
      
      const updatedIdentification = {
        type: profile?.identification?.type || '',
        number: profile?.identification?.number || '',
        frontImageUrl: downloadURL,
        backImageUrl: profile?.identification?.backImageUrl || '',
        verificationStatus: profile?.identification?.verificationStatus || 'pending',
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        identification: updatedIdentification
      });
      
      setProfile(prev => prev ? { 
        ...prev, 
        identification: updatedIdentification 
      } : null);
      
      setSuccess(t('profile.success.photoUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error uploading ID front image:', error);
      setError(t('profile.error.photoUpload'));
      setTimeout(() => setError(''), 5000);
    } finally {
      setUploadingIdFront(false);
    }
  };

  const handleIdBackUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentUser || !e.target.files?.[0]) return;
    
    try {
      setUploadingIdBack(true);
      const file = e.target.files[0];
      const fileExtension = file.name.split('.').pop();
      const fileName = `back_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `users/${currentUser.uid}/id/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Eliminar imagen anterior si existe
      if (profile?.identification?.backImageUrl) {
        try {
          const oldImageRef = ref(storage, profile.identification.backImageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.log('No previous image to delete');
        }
      }
      
      const updatedIdentification = {
        type: profile?.identification?.type || '',
        number: profile?.identification?.number || '',
        frontImageUrl: profile?.identification?.frontImageUrl || '',
        backImageUrl: downloadURL,
        verificationStatus: profile?.identification?.verificationStatus || 'pending',
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        identification: updatedIdentification
      });
      
      setProfile(prev => prev ? { 
        ...prev, 
        identification: updatedIdentification 
      } : null);
      
      setSuccess(t('profile.success.photoUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error uploading ID back image:', error);
      setError(t('profile.error.photoUpload'));
      setTimeout(() => setError(''), 5000);
    } finally {
      setUploadingIdBack(false);
    }
  };

  // Función para iniciar proceso de subida de certificado
  const handleDigitalSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Validar que sea archivo .p12
    if (!file.name.endsWith('.p12') && !file.name.endsWith('.pfx')) {
      setError(t('profile.digitalSignature.errorInvalidFile'));
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Guardar el archivo y mostrar modal de contraseña
    setPendingCertificateFile(file);
    setCertificatePassword('');
    setShowPasswordModal(true);
  };

  // Función para validar y procesar el certificado con la contraseña
  const validateAndUploadCertificate = async () => {
    if (!pendingCertificateFile || !currentUser) return;

    try {
      setValidatingCertificate(true);
      setError('');

      // Leer el archivo como ArrayBuffer
      const arrayBuffer = await pendingCertificateFile.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const binaryString = String.fromCharCode.apply(null, Array.from(bytes));

      // Intentar decodificar el certificado PKCS#12 con la contraseña
      let p12Asn1;
      try {
        p12Asn1 = forge.asn1.fromDer(binaryString);
      } catch (err) {
        setError(t('profile.digitalSignature.errorInvalidFormat'));
        setValidatingCertificate(false);
        return;
      }

      let p12;
      try {
        p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, certificatePassword);
      } catch (err) {
        setError(t('profile.digitalSignature.errorInvalidPassword'));
        setValidatingCertificate(false);
        return;
      }

      // Extraer el certificado
      const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
      const certBag = certBags[forge.pki.oids.certBag]?.[0];
      
      if (!certBag || !certBag.cert) {
        setError(t('profile.digitalSignature.errorNoCertificate'));
        setValidatingCertificate(false);
        return;
      }

      const certificate = certBag.cert;

      // Extraer información del certificado
      const subject = certificate.subject.attributes;
      const issuer = certificate.issuer.attributes;
      const validFrom = certificate.validity.notBefore;
      const validTo = certificate.validity.notAfter;
      const now = new Date();

      // Determinar el estado del certificado
      let status: 'active' | 'expired' | 'revoked' = 'active';
      if (now > validTo) {
        status = 'expired';
        setError(t('profile.digitalSignature.errorExpired'));
        setValidatingCertificate(false);
        return;
      } else if (now < validFrom) {
        setError(t('profile.digitalSignature.errorNotYetValid'));
        setValidatingCertificate(false);
        return;
      }

      // Extraer campos del subject
      const getAttributeValue = (attrs: any[], oid: string) => {
        const attr = attrs.find(a => a.type === oid || a.shortName === oid);
        return attr?.value || '';
      };

      const commonName = getAttributeValue(subject, 'CN');
      const organization = getAttributeValue(subject, 'O');
      const country = getAttributeValue(subject, 'C');
      const issuerName = getAttributeValue(issuer, 'CN');

      setUploadingCertificate(true);
      setShowPasswordModal(false);

      // Eliminar certificado anterior si existe
      if (profile?.digitalSignature?.certificateUrl) {
        try {
          const oldCertRef = ref(storage, profile.digitalSignature.certificateUrl);
          await deleteObject(oldCertRef);
        } catch (err) {
          console.log('No previous certificate to delete');
        }
      }

      // Subir nuevo certificado
      const certRef = ref(storage, `users/${currentUser.uid}/signature/${Date.now()}_${pendingCertificateFile.name}`);
      await uploadBytes(certRef, pendingCertificateFile);
      const certUrl = await getDownloadURL(certRef);

      // Actualizar perfil con información detallada del certificado
      const digitalSignatureData = {
        certificateUrl: certUrl,
        certificateName: pendingCertificateFile.name,
        uploadedAt: new Date().toISOString(),
        status: status,
        subjectName: commonName,
        organization: organization,
        country: country,
        issuerName: issuerName,
        validFrom: validFrom.toISOString(),
        validTo: validTo.toISOString(),
        expiryDate: validTo.toISOString(),
        serialNumber: certificate.serialNumber
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        digitalSignature: digitalSignatureData,
        updatedAt: new Date().toISOString()
      });

      setProfile(prev => prev ? { ...prev, digitalSignature: digitalSignatureData } : null);
      setEditData(prev => ({ ...prev, digitalSignature: digitalSignatureData }));
      setSuccess(t('profile.digitalSignature.uploadSuccess'));
      
      setPendingCertificateFile(null);
      setCertificatePassword('');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error validating/uploading certificate:', err);
      setError(err.message || t('profile.digitalSignature.uploadError'));
    } finally {
      setUploadingCertificate(false);
      setValidatingCertificate(false);
    }
  };

  const handleDeleteDigitalSignature = async () => {
    if (!currentUser || !profile?.digitalSignature?.certificateUrl) return;

    if (!confirm(t('profile.digitalSignature.confirmDelete'))) {
      return;
    }

    try {
      setUploadingCertificate(true);

      // Eliminar archivo de storage
      const certRef = ref(storage, profile.digitalSignature.certificateUrl);
      await deleteObject(certRef);

      // Actualizar Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        digitalSignature: null,
        updatedAt: new Date().toISOString()
      });

      setProfile(prev => prev ? { ...prev, digitalSignature: undefined } : null);
      setEditData(prev => ({ ...prev, digitalSignature: undefined }));
      setSuccess(t('profile.digitalSignature.deleteSuccess'));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('profile.digitalSignature.deleteError'));
      console.error('Error deleting digital signature:', err);
    } finally {
      setUploadingCertificate(false);
    }
  };

  // Función para generar PDF del perfil público




  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{t('profile.error.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => {
              // Redirigir según el rol del usuario
              if (userData?.role === 'business') {
                navigate('/business/dashboard');
              } else if (userData?.role === 'employee') {
                navigate('/employee-dashboard');
              } else if (userData?.role === 'admin') {
                navigate('/admin-dashboard');
              } else {
                navigate('/worker-dashboard');
              }
            }}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <FaArrowLeft />
            <span>{t('common.back')}</span>
          </button>
          
          <div className="flex items-center gap-4">
            {!isEditing && (
              <div key="viewEditButtons" className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/worker/${currentUser?.uid}`)}
                  className="flex items-center gap-2 bg-white text-red-600 border-2 border-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <FaEye />
                  <span>{t('profile.viewPublicProfile')}</span>
                </button>
                
                                      <ExportPDFButton 
                    profile={profile}
                    userId={currentUser?.uid}
                    variant="primary"
                    size="md"
                  />
                  
                  {/* Botón para regenerar QR code */}
                  <button
                    onClick={generateAndSaveQRCode}
                    className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    title="Regenerar QR Code"
                  >
                    <FaQrcode />
                    <span className="hidden sm:inline">Regenerar QR</span>
                  </button>
                
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FaEdit />
                  <span>{t('common.edit')}</span>
                </button>
              </div>
            )}
            
            {isEditing && (
              <div key="editButtons" className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <FaSave />
                  <span>{saving ? t('common.saving') : t('common.save')}</span>
                </button>
                
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <FaTimes />
                  <span>{t('common.cancel')}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        <div key="mainGrid" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Columna izquierda - Información personal */}
          <div key="leftColumn" className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
              {/* Foto de perfil */}
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-red-200 mx-auto mb-4">
                    {profile.photoURL ? (
                      <img 
                        src={profile.photoURL} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${profile.photoURL ? 'hidden' : ''}`}>
                      <FaUser className="text-4xl text-gray-400" />
                    </div>
                  </div>
                  
                  {isEditing && (
                    <label className="absolute bottom-0 right-0 bg-red-600 text-white p-2 rounded-full cursor-pointer hover:bg-red-700 transition-colors">
                      <FaCamera />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                    </label>
                  )}
                </div>
                
                {uploadingPhoto && (
                  <p className="text-sm text-gray-600">{t('profile.uploadingPhoto')}</p>
                )}
              </div>

              {/* QR Code */}
              <div className="text-center mb-6">
                <QRCodeSVG 
                  value={currentUser?.uid || ''} 
                  size={120} 
                  level="H" 
                  includeMargin={true}
                  className="mx-auto"
                />
                <p className="text-xs text-gray-500 mt-2">{t('profile.qrCode')}</p>
              </div>

              {/* Información básica */}
              <div className="space-y-4">
                <div key="basicInfo" className="text-center">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {profile.firstName} {profile.lastName}
                  </h2>
                  <p className="text-gray-600">{profile.email}</p>
                </div>

                <div key="sectorInfo" className="flex items-center justify-center gap-2">
                  {profile.sector === 'health' ? (
                    <FaRegHospital className="text-2xl text-red-500" />
                  ) : (
                    <FaHardHat className="text-2xl text-yellow-500" />
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {t(`profile.sectors.${profile.sector}`)}
                  </span>
                </div>

                <div key="statusInfo" className="flex items-center justify-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    profile.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {t(`profile.status.${profile.status}`)}
                  </span>
                </div>
              </div>
            </div>

            {/* Estadísticas */}
            <div key="statisticsSection" className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('profile.statistics')}</h3>
              <div key="statisticsList" className="space-y-4">
                <div key="completedJobs" className="flex justify-between items-center">
                  <span className="text-gray-600">{t('profile.completedJobs')}</span>
                  <span className="font-bold text-blue-600">{profile.completedJobs || 0}</span>
                </div>
                <div key="rating" className="flex justify-between items-center">
                  <span className="text-gray-600">{t('profile.rating')}</span>
                  <div key="ratingDisplay" className="flex items-center gap-1">
                    <span className="font-bold text-yellow-600">{profile.rating || 0}</span>
                    <FaStar className="text-yellow-500 text-sm" />
                    <span className="text-sm text-gray-500">({profile.reviews || 0})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Columna derecha - Detalles y certificaciones */}
          <div key="rightColumn" className="lg:col-span-2 space-y-6">
            {/* Información personal editable */}
            <div key="personalInfoSection" className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaUser />
                {t('profile.personalInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div key="firstName">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.firstName')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.firstName || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.firstName}</p>
                  )}
                </div>

                <div key="lastName">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.lastName')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.lastName || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.lastName}</p>
                  )}
                </div>

                <div key="email">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <FaEnvelope />
                    {t('profile.email')}
                  </label>
                  <p className="text-gray-800">{profile.email}</p>
                </div>

                <div key="phone">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <FaPhone />
                    {t('profile.phone')}
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editData.phone || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Descripción Personal */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaUser />
                {t('profile.personalDescription')}
              </h3>
              
              <div>
                {isEditing ? (
                  <textarea
                    value={editData.personalDescription || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, personalDescription: e.target.value }))}
                    placeholder={t('profile.personalDescriptionPlaceholder')}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-800">
                    {profile.personalDescription || t('profile.personalDescriptionPlaceholder')}
                  </p>
                )}
              </div>
            </div>

            {/* Dirección */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaMapMarkerAlt />
                {t('profile.address')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div key="street" className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.street')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address?.street || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        address: { 
                          street: e.target.value,
                          city: prev.address?.city || profile.address?.city || '',
                          state: prev.address?.state || profile.address?.state || '',
                          zipCode: prev.address?.zipCode || profile.address?.zipCode || '',
                          country: prev.address?.country || profile.address?.country || '',
                          apartment: prev.address?.apartment,
                          number: prev.address?.number
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.address?.street}</p>
                  )}
                </div>

                <div key="city">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.city')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address?.city || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        address: { 
                          street: prev.address?.street || profile.address?.street || '',
                          city: e.target.value,
                          state: prev.address?.state || profile.address?.state || '',
                          zipCode: prev.address?.zipCode || profile.address?.zipCode || '',
                          country: prev.address?.country || profile.address?.country || '',
                          apartment: prev.address?.apartment,
                          number: prev.address?.number
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.address?.city}</p>
                  )}
                </div>

                <div key="state">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.state')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address?.state || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        address: { 
                          street: prev.address?.street || profile.address?.street || '',
                          city: prev.address?.city || profile.address?.city || '',
                          state: e.target.value,
                          zipCode: prev.address?.zipCode || profile.address?.zipCode || '',
                          country: prev.address?.country || profile.address?.country || '',
                          apartment: prev.address?.apartment,
                          number: prev.address?.number
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.address?.state}</p>
                  )}
                </div>

                <div key="zipCode">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.zipCode')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address?.zipCode || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        address: { 
                          street: prev.address?.street || profile.address?.street || '',
                          city: prev.address?.city || profile.address?.city || '',
                          state: prev.address?.state || profile.address?.state || '',
                          zipCode: e.target.value,
                          country: prev.address?.country || profile.address?.country || '',
                          apartment: prev.address?.apartment,
                          number: prev.address?.number
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.address?.zipCode}</p>
                  )}
                </div>

                <div key="country">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.country')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address?.country || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        address: { 
                          street: prev.address?.street || profile.address?.street || '',
                          city: prev.address?.city || profile.address?.city || '',
                          state: prev.address?.state || profile.address?.state || '',
                          zipCode: prev.address?.zipCode || profile.address?.zipCode || '',
                          country: e.target.value,
                          apartment: prev.address?.apartment,
                          number: prev.address?.number
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.address?.country}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Experiencia */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaBriefcase />
                {t('profile.experience')}
              </h3>
              
              <div className="space-y-4">
                <div key="yearsExperience">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.yearsExperience')}
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editData.experience?.years || 0}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        experience: { 
                          years: parseInt(e.target.value),
                          skills: prev.experience?.skills || profile.experience?.skills || [],
                          description: prev.experience?.description || profile.experience?.description || ''
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.experience?.years || 0} {t('profile.years')}</p>
                  )}
                </div>

                <div key="experienceDescription">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.description')}
                  </label>
                  {isEditing ? (
                    <textarea
                      value={editData.experience?.description || ''}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        experience: { 
                          years: prev.experience?.years || profile.experience?.years || 0,
                          skills: prev.experience?.skills || profile.experience?.skills || [],
                          description: e.target.value
                        }
                      }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-800">{profile.experience?.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Certificaciones */}
            {userData?.role !== 'business' && (
            <div key="certificationsSection" className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FaGraduationCap />
                  {t('profile.certifications')}
                </h3>
                

              </div>

              {/* Lista de certificaciones */}
              <div key="certificationsList" className="space-y-4">
                {profile.certifications && profile.certifications.length > 0 ? (
                  <div key="certificationsMap">
                    {profile.certifications.map((cert) => (
                      <div key={cert.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div key={`certHeader-${cert.id}`} className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-800">{cert.name}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                cert.status === 'approved' ? 'bg-green-100 text-green-800' :
                                cert.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {t(`profile.certificationStatus.${cert.status}`)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">{t('profile.issuer')}: {cert.issuer}</p>
                            <p className="text-sm text-gray-600 mb-1">{t('profile.issueDate')}: {cert.date}</p>
                            {cert.expiryDate && (
                              <p key={`expiry-${cert.id}`} className="text-sm text-gray-600 mb-2">{t('profile.expiryDate')}: {cert.expiryDate}</p>
                            )}
                          </div>
                          
                          <div key={`certActions-${cert.id}`} className="flex items-center gap-2">
                            <a
                              href={cert.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-600 hover:text-red-700"
                            >
                              <FaDownload />
                            </a>
                            

                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div key="noCertifications" className="text-center py-8">
                    <FaGraduationCap className="text-gray-400 text-4xl mx-auto mb-2" />
                    <p className="text-gray-600 font-medium">{t('publicProfile.noCertifications')}</p>
                    <p className="text-gray-500 text-sm">{t('skills.noSkillsDescription')}</p>
                  </div>
                )}
              </div>


            </div>
            )}

            {/* Identificación */}
            {userData?.role !== 'business' && (
            <div key="identificationSection" className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaIdCard />
                {t('profile.identification')}
              </h3>
              
              {profile.identification ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div key="idType">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.idType')}
                      </label>
                      {isEditing ? (
                        <select
                          value={editData.identification?.type || profile.identification.type}
                          onChange={(e) => setEditData(prev => ({ 
                            ...prev, 
                            identification: { 
                              type: e.target.value,
                              number: prev.identification?.number || profile.identification?.number || '',
                              frontImageUrl: prev.identification?.frontImageUrl || profile.identification?.frontImageUrl || '',
                              backImageUrl: prev.identification?.backImageUrl || profile.identification?.backImageUrl || '',
                              verificationStatus: prev.identification?.verificationStatus || profile.identification?.verificationStatus || 'pending',
                              updatedAt: new Date().toISOString()
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                          <option value="driver_license">{t('profile.driverLicense')}</option>
                          <option value="passport">{t('profile.passport')}</option>
                          <option value="national_id">{t('profile.nationalId')}</option>
                          <option value="other">{t('profile.other')}</option>
                        </select>
                      ) : (
                        <p className="text-gray-800">
                          {profile.identification.type === 'driver_license' ? t('profile.driverLicense') :
                           profile.identification.type === 'passport' ? t('profile.passport') :
                           profile.identification.type === 'national_id' ? t('profile.nationalId') :
                           t('profile.other')}
                        </p>
                      )}
                    </div>

                    <div key="idNumber">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.idNumber')}
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.identification?.number || profile.identification.number}
                          onChange={(e) => setEditData(prev => ({ 
                            ...prev, 
                            identification: { 
                              type: prev.identification?.type || profile.identification?.type || '',
                              number: e.target.value,
                              frontImageUrl: prev.identification?.frontImageUrl || profile.identification?.frontImageUrl || '',
                              backImageUrl: prev.identification?.backImageUrl || profile.identification?.backImageUrl || '',
                              verificationStatus: prev.identification?.verificationStatus || profile.identification?.verificationStatus || 'pending',
                              updatedAt: new Date().toISOString()
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-800">{profile.identification.number}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div key="frontImage">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('profile.frontImage')}
                      </label>
                      <div className="border border-gray-200 rounded-lg p-2">
                        <img
                          src={profile.identification.frontImageUrl}
                          alt="Front ID"
                          className="w-full h-32 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-32 bg-gray-100 flex items-center justify-center rounded">
                          <FaImage className="text-gray-400 text-2xl" />
                        </div>
                      </div>
                      {isEditing && (
                        <div className="mt-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleIdFrontUpload}
                            className="hidden"
                            id="id-front-upload"
                            disabled={uploadingIdFront}
                          />
                          <label
                            htmlFor="id-front-upload"
                            className={`flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                              uploadingIdFront ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <FaCamera />
                            <span>{uploadingIdFront ? t('common.uploading') : t('common.change')}</span>
                          </label>
                        </div>
                      )}
                    </div>

                    <div key="backImage">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('profile.backImage')}
                      </label>
                      <div className="border border-gray-200 rounded-lg p-2">
                        <img
                          src={profile.identification.backImageUrl}
                          alt="Back ID"
                          className="w-full h-32 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-32 bg-gray-100 flex items-center justify-center rounded">
                          <FaImage className="text-gray-400 text-2xl" />
                        </div>
                      </div>
                      {isEditing && (
                        <div className="mt-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleIdBackUpload}
                            className="hidden"
                            id="id-back-upload"
                            disabled={uploadingIdBack}
                          />
                          <label
                            htmlFor="id-back-upload"
                            className={`flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                              uploadingIdBack ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <FaCamera />
                            <span>{uploadingIdBack ? t('common.uploading') : t('common.change')}</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>

                  <div key="verificationStatus">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.verificationStatus')}
                    </label>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      profile.identification.verificationStatus === 'approved' ? 'bg-green-100 text-green-800' :
                      profile.identification.verificationStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {t(`profile.certificationStatus.${profile.identification.verificationStatus}`)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaIdCard className="text-gray-400 text-4xl mx-auto mb-2" />
                  <p className="text-gray-600 font-medium">{t('profile.noIdentification')}</p>
                </div>
              )}
            </div>
            )}

            {/* Habilidades */}
            {userData?.role !== 'business' && (
            <div key="skillsSection" className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FaTools />
                  {t('skills.title')}
                </h3>
              </div>

              {/* Lista de habilidades */}
              <div key="skillsList" className="space-y-4">
                {profile.experience?.skills && profile.experience.skills.length > 0 ? (
                  profile.experience.skills.map((skill) => (
                    <div key={skill.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div key={`skillHeader-${skill.id}`} className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-800">
                              {i18n.language === 'es' ? skill.name_es : skill.name_en}
                            </h4>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getCategoryName(skill.categoryId)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FaTools className="text-gray-400 text-4xl mx-auto mb-2" />
                    <p className="text-gray-600 font-medium">{t('skills.noSkills')}</p>
                    <p className="text-gray-500 text-sm">{t('skills.noSkillsDescription')}</p>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Certificado de Firma Electrónica */}
            <div key="digitalSignatureSection" className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaFileSignature className="text-blue-600" />
                {t('profile.digitalSignature.title')}
              </h3>
              
              <div className="space-y-4">
                {profile.digitalSignature ? (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <FaCheckCircle className="text-green-600 text-xl mt-1" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 mb-1">
                              {profile.digitalSignature.certificateName}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              {t('profile.digitalSignature.uploaded')}: {new Date(profile.digitalSignature.uploadedAt).toLocaleDateString()}
                            </p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              profile.digitalSignature.status === 'active' ? 'bg-green-100 text-green-800' :
                              profile.digitalSignature.status === 'expired' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(`profile.digitalSignature.status.${profile.digitalSignature.status}`)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Información detallada del certificado */}
                        {(profile.digitalSignature.subjectName || profile.digitalSignature.organization) && (
                          <div className="ml-8 mt-3 space-y-2 border-t border-green-200 pt-3">
                            {profile.digitalSignature.subjectName && (
                              <div className="flex items-start">
                                <span className="text-xs font-semibold text-gray-600 w-28">{t('profile.digitalSignature.certInfo.subject')}:</span>
                                <span className="text-xs text-gray-700">{profile.digitalSignature.subjectName}</span>
                              </div>
                            )}
                            {profile.digitalSignature.organization && (
                              <div className="flex items-start">
                                <span className="text-xs font-semibold text-gray-600 w-28">{t('profile.digitalSignature.certInfo.organization')}:</span>
                                <span className="text-xs text-gray-700">{profile.digitalSignature.organization}</span>
                              </div>
                            )}
                            {profile.digitalSignature.issuerName && (
                              <div className="flex items-start">
                                <span className="text-xs font-semibold text-gray-600 w-28">{t('profile.digitalSignature.certInfo.issuer')}:</span>
                                <span className="text-xs text-gray-700">{profile.digitalSignature.issuerName}</span>
                              </div>
                            )}
                            {profile.digitalSignature.validFrom && profile.digitalSignature.validTo && (
                              <div className="flex items-start">
                                <span className="text-xs font-semibold text-gray-600 w-28">{t('profile.digitalSignature.certInfo.validity')}:</span>
                                <span className="text-xs text-gray-700">
                                  {new Date(profile.digitalSignature.validFrom).toLocaleDateString()} - {new Date(profile.digitalSignature.validTo).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {profile.digitalSignature.serialNumber && (
                              <div className="flex items-start">
                                <span className="text-xs font-semibold text-gray-600 w-28">{t('profile.digitalSignature.certInfo.serial')}:</span>
                                <span className="text-xs text-gray-700 font-mono">{profile.digitalSignature.serialNumber}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {isEditing && (
                        <div className="flex gap-2">
                          <a
                            href={profile.digitalSignature.certificateUrl}
                            download={profile.digitalSignature.certificateName}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                            title={t('profile.digitalSignature.download')}
                          >
                            <FaDownload />
                          </a>
                          <button
                            onClick={handleDeleteDigitalSignature}
                            disabled={uploadingCertificate}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title={t('profile.digitalSignature.delete')}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <FaFileSignature className="text-gray-400 text-4xl mx-auto mb-3" />
                    <p className="text-gray-600 font-medium mb-2">
                      {t('profile.digitalSignature.noCertificate')}
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      {t('profile.digitalSignature.uploadDescription')}
                    </p>
                    
                    {isEditing && (
                      <div>
                        <input
                          type="file"
                          accept=".p12"
                          onChange={handleDigitalSignatureUpload}
                          className="hidden"
                          id="digital-signature-upload"
                          disabled={uploadingCertificate}
                        />
                        <label
                          htmlFor="digital-signature-upload"
                          className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors ${
                            uploadingCertificate ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <FaUpload />
                          <span>{uploadingCertificate ? t('common.uploading') : t('profile.digitalSignature.uploadButton')}</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <FaFileSignature className="text-blue-600" />
                    {t('profile.digitalSignature.infoTitle')}
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>{t('profile.digitalSignature.info1')}</li>
                    <li>{t('profile.digitalSignature.info2')}</li>
                    <li>{t('profile.digitalSignature.info3')}</li>
                    <li>{t('profile.digitalSignature.info4')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para contraseña del certificado */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaFileSignature className="text-blue-600" />
              {t('profile.digitalSignature.passwordModal.title')}
            </h3>
            
            <p className="text-gray-600 text-sm mb-4">
              {t('profile.digitalSignature.passwordModal.description')}
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.digitalSignature.passwordModal.password')}
              </label>
              <input
                type="password"
                value={certificatePassword}
                onChange={(e) => setCertificatePassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && validateAndUploadCertificate()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('profile.digitalSignature.passwordModal.passwordPlaceholder')}
                autoComplete="off"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPendingCertificateFile(null);
                  setCertificatePassword('');
                  setError('');
                }}
                disabled={validatingCertificate}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={validateAndUploadCertificate}
                disabled={!certificatePassword || validatingCertificate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {validatingCertificate ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('profile.digitalSignature.passwordModal.validating')}
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    {t('profile.digitalSignature.passwordModal.validate')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerProfile; 